import { useEffect, useState } from 'react'
import { ApiError, KIND_TEXT } from '../net/errors.js'

/**
 * Meldungsleiste – eine feste Stelle, nicht mal oben, mal im Formular.
 *
 * Zwei Regeln, die hier durchgesetzt werden:
 *
 *  1. Kein roher Netzwerkfehler erreicht den Benutzer. Jede Meldung sagt,
 *     was als Nächstes zu tun ist.
 *  2. Kein Erfolgshinweis für Selbstverständliches. Wenn jede gespeicherte
 *     Zeile eine Meldung auslöst, liest nach einer Woche niemand mehr eine –
 *     auch die wichtige nicht.
 */

export type ToastTone = 'ok' | 'warn' | 'error' | 'info'

export interface Toast {
  id: number
  tone: ToastTone
  text: string
  /** Was der Benutzer jetzt tun kann. */
  action?: { label: string; run: () => void }
  timeoutMs: number
}

type Listener = (toasts: Toast[]) => void

const listeners = new Set<Listener>()
let toasts: Toast[] = []
let nextId = 1

function publish(): void {
  for (const l of listeners) l(toasts)
}

/**
 * Höchstens so viele Meldungen gleichzeitig.
 *
 * Die Leiste steht fest am unteren Rand. Auf dem Telefon deckt schon die
 * dritte Meldung die Knöpfe darunter zu – und wer sie nicht sieht, kann sie
 * auch nicht wegklicken.
 */
const HOECHSTENS = 3

function push(toast: Omit<Toast, 'id'>): number {
  /*
    Dieselbe Meldung stapelt sich nicht.

    Gemeldet aus dem Kundenbetrieb: Zweimal «An eigene Adresse senden», und
    derselbe Satz stand zweimal übereinander über den Aktionen. Fehler bleiben
    stehen, bis sie jemand wegklickt (`timeoutMs: 0`) – zwei Versuche sind
    damit zwei Meldungen, drei Versuche drei, und am Ende ist die Karte
    darunter nicht mehr lesbar.

    Ein zweiter Versuch, der genauso scheitert, ist keine zweite Nachricht.
    Die vorhandene Meldung bleibt – sie wandert ans Ende, damit sichtbar ist,
    dass es eben wieder passiert ist, und ihr Zeitgeber beginnt von vorn.
    Verglichen wird, was der Benutzer liest: Ton und Text.
  */
  const gleich = toasts.find((t) => t.tone === toast.tone && t.text === toast.text)
  if (gleich) {
    toasts = [...toasts.filter((t) => t.id !== gleich.id), { ...gleich, action: toast.action }]
    publish()
    if (toast.timeoutMs > 0) {
      setTimeout(() => dismiss(gleich.id), toast.timeoutMs)
    }
    return gleich.id
  }

  const id = nextId++
  /* Die ältesten weichen, nicht die neuesten – die neue Meldung ist die, die gilt. */
  toasts = [...toasts, { ...toast, id }].slice(-HOECHSTENS)
  publish()
  if (toast.timeoutMs > 0) {
    setTimeout(() => dismiss(id), toast.timeoutMs)
  }
  return id
}

export function dismiss(id: number): void {
  toasts = toasts.filter((t) => t.id !== id)
  publish()
}

export function clearToasts(): void {
  toasts = []
  publish()
}

export const notify = {
  /** Nur für Dinge, die nicht ohnehin sichtbar sind. */
  ok: (text: string) => push({ tone: 'ok', text, timeoutMs: 3_000 }),
  info: (text: string) => push({ tone: 'info', text, timeoutMs: 4_000 }),
  warn: (text: string, action?: Toast['action']) =>
    push({ tone: 'warn', text, action, timeoutMs: 8_000 }),
  /** Fehler bleiben stehen, bis der Benutzer sie wegklickt. */
  error: (text: string, action?: Toast['action']) =>
    push({ tone: 'error', text, action, timeoutMs: 0 })
}

/**
 * Der einzige erlaubte Weg, einen Fehler anzuzeigen. Übersetzt ApiError in
 * eine Meldung, die sagt, was zu tun ist – und fängt alles andere ab, damit
 * nie ein roher Fehlertext durchrutscht.
 */
export function notifyError(err: unknown, action?: Toast['action']): number {
  if (err instanceof ApiError) {
    if (err.kind === 'validation' && err.options.fields?.length) {
      const list = err.options.fields.map((f) => `${f.feld}: ${f.problem}`).join(' · ')
      return notify.error(`${err.message} ${list}`, action)
    }
    // Offline ist kein Fehler, solange lokal gespeichert werden konnte.
    if (err.kind === 'network') return notify.info(KIND_TEXT.network)
    return notify.error(err.message, action)
  }
  return notify.error('Da ist etwas schiefgelaufen. Der Fehler wurde protokolliert.', action)
}

export function Toasts(): JSX.Element {
  const [items, setItems] = useState<Toast[]>(toasts)

  useEffect(() => {
    listeners.add(setItems)
    return () => {
      listeners.delete(setItems)
    }
  }, [])

  if (items.length === 0) return <div className="toasts" aria-live="polite" />

  return (
    <div className="toasts" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`toast tone-${t.tone}`} role={t.tone === 'error' ? 'alert' : 'status'}>
          <span className="toast-text">{t.text}</span>
          {t.action && (
            <button type="button" className="toast-action" onClick={t.action.run}>
              {t.action.label}
            </button>
          )}
          <button
            type="button"
            className="toast-close"
            aria-label="Meldung schliessen"
            onClick={() => dismiss(t.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  )
}
